import { useCallback, useEffect, useState } from 'react';

import { clearHistory, deleteHistory, listRecentHistory } from '@/db/history';
import { subscribeAccountDataRestored } from '@/features/account/accountDataEvents';
import { requestAccountDataAutoSync } from '@/features/account/accountDataAutoSync';
import type { HistoryRow } from '@/db/schema';

/**
 * `last-seen` desc reading history with simple "reload" / "remove" handlers.
 *
 * Two consumers:
 *   1. `<ContinueReading />` on Home — wants the top N entries flat
 *   2. `<History />` page — wants the full list, grouped by date in render
 */
export function useHistory(limit = 100): {
  rows: HistoryRow[];
  loading: boolean;
  reload: () => void;
  remove: (source: string, comicId: string) => Promise<void>;
  clear: () => Promise<void>;
} {
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listRecentHistory(limit)
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [limit, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => subscribeAccountDataRestored(reload), [reload]);

  const remove = useCallback(
    async (source: string, comicId: string) => {
      await deleteHistory(source, comicId);
      requestAccountDataAutoSync();
      reload();
    },
    [reload],
  );

  const clear = useCallback(async () => {
    await clearHistory();
    requestAccountDataAutoSync();
    reload();
  }, [reload]);

  return { rows, loading, reload, remove, clear };
}

export type DateBucket = 'today' | 'yesterday' | 'this-week' | 'this-month' | 'older';

export interface HistoryGroup {
  bucket: DateBucket;
  label: string;
  rows: HistoryRow[];
}

const DAY_MS = 86_400_000;

/**
 * Bucket history rows into date groups for the History page. Pure function
 * keyed on `now` so tests can drive it deterministically; production callers
 * pass `Date.now()`.
 */
export function groupHistoryByDate(rows: HistoryRow[], now: number = Date.now()): HistoryGroup[] {
  const today = startOfDay(now);
  const yesterday = today - DAY_MS;
  const weekAgo = today - 7 * DAY_MS;
  const monthAgo = today - 30 * DAY_MS;

  const buckets = new Map<DateBucket, HistoryRow[]>([
    ['today', []],
    ['yesterday', []],
    ['this-week', []],
    ['this-month', []],
    ['older', []],
  ]);

  for (const row of rows) {
    const day = startOfDay(row.lastSeen);
    let bucket: DateBucket;
    if (day >= today) bucket = 'today';
    else if (day >= yesterday) bucket = 'yesterday';
    else if (day >= weekAgo) bucket = 'this-week';
    else if (day >= monthAgo) bucket = 'this-month';
    else bucket = 'older';
    buckets.get(bucket)!.push(row);
  }

  const labels: Record<DateBucket, string> = {
    today: 'Today',
    yesterday: 'Yesterday',
    'this-week': 'Earlier this week',
    'this-month': 'Earlier this month',
    older: 'Older',
  };

  const order: DateBucket[] = ['today', 'yesterday', 'this-week', 'this-month', 'older'];
  return order
    .map((b) => ({ bucket: b, label: labels[b], rows: buckets.get(b)! }))
    .filter((g) => g.rows.length > 0);
}

function startOfDay(ts: number): number {
  const d = new Date(ts);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}
